// Leaders.js
// -----------------------------------------------------------------------------
// "Meet our Church Leadership" page. Warm, friendly layout: hero, a featured
// lead-pastor block, then a responsive grid of elder cards. All real names,
// photos, and bio copy preserved from the original page.
// -----------------------------------------------------------------------------

import AspectRatio from '@mui/joy/AspectRatio';
import Box from '@mui/joy/Box';
import Grid from '@mui/joy/Grid';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import { Section, SectionHeading, PageHero } from '../../components/ui';
import LeadershipCard from '../../components/LeadershipCard';

import './leaders.css';
import Joshua from '../../assets/Joshua.jpg';
import Betsy from '../../assets/Besty.jpeg';
import Brandy from '../../assets/Brandy.jpeg';
import Vince from '../../assets/Vince.jpeg';
import usePageMeta from '../../hooks/usePageMeta';

// Module-private leadership data. Local to this page; not exported. Keeping it
// as data lets us render the elders grid by mapping rather than duplicating
// markup. (Scope note: file-private const, narrowest home that serves render.)
const ELDERS = [
  { image: Vince, name: 'Vince Saroni', title: 'Elder' },
  { image: Betsy, name: 'Betsy Saroni', title: 'Elder' },
  { image: Brandy, name: 'Brandy Stigler', title: 'Elder' },
];

const Leaders = () => {
  usePageMeta(
    'Our Leadership',
    'Meet Lead Pastor Joshua Ball and the elders who help shepherd New Horizon ' +
      'Vineyard in Rowlett, TX.',
  );

  return (
    <Box>
      <PageHero
        eyebrow="Who We Are"
        title="Meet Our Church Leadership"
        subtitle="Real people who love Jesus, love this city, and would love to know you. There's always room for one more at the table."
        height="sm"
        overlay="strong"
      />

      <Section bg="default" padding="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 8 }}
          sx={{ alignItems: 'center' }}
        >
          <Box sx={{ flex: 1, width: '100%' }}>
            <AspectRatio
              ratio="4/5"
              sx={{ borderRadius: 'lg', boxShadow: 'md', maxWidth: 460, mx: { xs: 'auto', md: 0 } }}
            >
              <img className="pastor-photo" src={Joshua} alt="Joshua Ball" loading="lazy" />
            </AspectRatio>
          </Box>

          <Stack spacing={2} sx={{ flex: 1.2 }}>
            <Typography level="title" sx={{ color: 'accent.700' }}>
              Lead Pastor
            </Typography>
            <Typography level="h3" sx={{ color: 'text.primary' }}>
              Joshua Ball
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              I&rsquo;m deeply passionate about sharing the good news of God&rsquo;s Kingdom and
              creating space for His presence to break into our lives in real and transformative
              ways. Nothing brings me more joy than equipping and encouraging others in their
              apprenticeship to Jesus, helping people grow in spiritual formation, discipleship, and
              the use of spiritual gifts. I love the Vineyard vision that &ldquo;everyone gets to
              play&rdquo; and believe that every follower of Jesus is invited to participate in the
              work of the Kingdom in naturally supernatural ways.
            </Typography>
            <Box
              sx={{
                borderLeft: '3px solid',
                borderColor: 'accent.400',
                pl: 2.5,
                py: 0.5,
              }}
            >
              <Typography level="body-md" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                I carry a strong heart for hospitality and genuine connection. I long to see the
                local church become a place of healing, growth, and transformation, a vibrant family
                where people are known, loved, and empowered to live out their calling. Around here,
                there&rsquo;s always room for one more at the table. Together, as a community, we seek
                to follow Jesus wholeheartedly, live out the Great Commission, and make disciples who
                bring hope and renewal to our city and beyond.
              </Typography>
            </Box>
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              In my rhythms of rest and play, I love spending time with my beautiful Brazilian wife
              and our two precious daughters who light up my life. I also enjoy building deep
              friendships, laughing over a good meal, and hosting spaces where people can belong. You
              might also find me playing sports, exercising, strumming my guitar, or as Nacho Libre
              once said, &ldquo;read some books!&rdquo;
            </Typography>
          </Stack>
        </Stack>
      </Section>

      <Section bg="tint" padding="lg">
        <SectionHeading
          align="center"
          eyebrow="Our Elders"
          title="Meet Our Elders"
          subtitle="A team that helps shepherd, guide, and care for the NHV family."
        />
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 8, md: 12 }}
          sx={{ justifyContent: 'center', mt: 2 }}
        >
          {ELDERS.map((elder) => (
            <Grid key={elder.name} xs={4} sm={4} md={4}>
              <LeadershipCard image={elder.image} name={elder.name} title={elder.title} />
            </Grid>
          ))}
        </Grid>
      </Section>
    </Box>
  );
};

export default Leaders;
